import React, { useContext, useEffect, useState } from "react";
import Switch from "@mui/material/Switch";
import { Icon } from "@iconify/react";
import { AuthContext } from "../../context/AuthContextProvider.js";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  updataOrgSettings,
  useGetOrgProfile,
  useGetIndianStates,
  useGetCitiesByState,
} from "../../api/settings";
import { toast } from "sonner";
import { Helmet } from "react-helmet";
import { useForm } from "react-hook-form";
import GoogleMapWithSearch from "../../components/GoogleMapWithSearch.jsx";
import { useNavigate } from "react-router-dom";
import CentersTreeView from "../../components/CentersTreeView.jsx";
import CreditBalanceCard from "../../components/Credits/CreditBalanceCard.jsx";
import TopUpCreditsModal from "../../components/Credits/TopUpCreditsModal.jsx";
import PaymentStatusModal from "../../components/Credits/PaymentStatusModal.jsx";
import useCreditWallet from "../../hooks/useCreditWallet.js";
import ImageUploader from "../../components/Uploader/ImageUploader.jsx";
import {
  Avatar,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  Select,
  MenuItem,
  Typography,
  Button,
  CircularProgress,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  Autocomplete,
} from "@mui/material";
import { IconButton, Box } from "@mui/material";
import ImageCropperDialog from "../../components/ImageCropper.jsx";
import FormTextField from "../../components/Reusables/FormControls/FormTextFeilds.jsx";
import FormCountryPicker from "../../components/Reusables/FormControls/FormCountryPicker";
import BillingDetails from "../../components/Reusables/BillingComponent/BillingDetails.jsx";
import { formatText } from "../../components/Reusables/TextValidation/SanitizeText.js";
import {
  getPhoneNumberLimit,
  getPhoneNumberLimitMessage,
  sanitizePhoneNumber,
} from "../../utils/phoneNumber.js";

import organisationType from "../../data/organisationType.json";
import CentreSelect from "../../components/CentreSelect.jsx";

const OrgSettings = () => {
  const [argOrg, setArgOrg] = useState({ reset: false });
  const { user, all_centres, open } = useContext(AuthContext);
  // Migrated businesses don't get the plan/credit/top-up UI - userType stays
  // plain "b2b", migration is a separate flag (see spandanMigrationController.js).
  const isMigratedUser = user?.userType === "b2b" && user?.migration === true;
  const [address, setAddress] = useState(false);
  const [billingAddress, setBillingAddress] = useState(false);
  const { orgProfile } = useGetOrgProfile(argOrg);
  const {
    credits: orgCredits,
    isUnlimitedCredits: orgIsUnlimitedCredits,
    currentPlan: orgCurrentPlan,
    autoSendEnabled: orgAutoSendEnabled,
    pricePerCredit: orgPricePerCredit,
    canTopUp: orgCanTopUp,
    topUpOpen: orgTopUpOpen,
    openTopUp: openOrgTopUp,
    closeTopUp: closeOrgTopUp,
    paying: orgTopUpPaying,
    handlePayTopUp: handleOrgPayTopUp,
    statusModal: orgStatusModal,
    closeStatusModal: closeOrgStatusModal,
    retryTopUp: retryOrgTopUp,
  } = useCreditWallet();
  const orgOutOfCredits = !orgIsUnlimitedCredits && Number(orgCredits) <= 0;
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isSameAsAddress, setIsSameAsAddress] = useState(false);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [isBillingManual, setIsBillingManual] = useState(false);

  const [cropOpen, setCropOpen] = useState(false);
  const [imageSrc, setImageSrc] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);

  const navigate = useNavigate();

  const defaultValues = {
    orgName: "",
    orgCountryCode: "+91",
    orgPhoneNumber: "",
    orgType: "",
    orgAddress: {
      floor: "",
      area: "",
      pincode: "",
      city: "",
      state: "",
      country: "",
      fulladdress: "",
      latitude: "",
      longitude: "",
      district: "",
    },
    orgBillingDetails: {
      name: "",
      taxId: "",
      pincode: "",
      state: "",
      city: "",
      stateCode: "",
      address: {
        floor: "",
        area: "",
        pincode: "",
        city: "",
        state: "",
        country: "",
        fulladdress: "",
        latitude: "",
        longitude: "",
        district: "",
      },
    },
    supportNumber: {
      phoneNumber: "",
      countryCode: "+91",
    },
    branding_by: "",
    autoSendReport: true,
    autoCaseCreation: true,
    areSymptomsMandatory: false,
    selectedCenters: [],
    autoSendTo: "",
    logo: null,
  };

  const ProfileSchema = Yup.object().shape({
    orgName: Yup.string()
      .required("Name is required")
      .min(2, "Name must be at least 2 characters")
      .max(150, "Name cannot exceed 150 characters"),
    // orgPhoneNumber: Yup.string()
    //   .required("Phone Number is required")
    //   .matches(/^\d{10}$/, "Phone number must be 10 digits"),
    orgType: Yup.string().required("Type is required"),
    orgAddress: Yup.object().shape({
      fulladdress: Yup.string()
        .required("Address is required")
        .min(10, "Address must be at least 10 characters"),
    }),
    orgBillingDetails: Yup.object().shape({
      name: Yup.string()
        .required("Name is required")
        .min(2, "Name must be at least 2 characters")
        .max(150, "Name cannot exceed 150 characters"),
      taxId: Yup.string().required("Tax ID is required"),
      pincode: Yup.string()
        .required("Pincode is required")
        .matches(/^\d{6}$/, "Pincode must be 6 digits"),
      state: Yup.string().required("State is required"),
      city: Yup.string()
        .required("City is required")
        .min(2, "City must be at least 2 characters"),
      stateCode: Yup.string()
        .required("Tax State Code is required")
        .min(2, "State code must be at least 2 characters"),
      address: Yup.object().shape({
        fulladdress: Yup.string()
          .required("Billing Address is required")
          .min(10, "Address must be at least 10 characters"),
      }),
    }),
    supportNumber: Yup.object().shape({
      phoneNumber: Yup.string()
        .required("Phone Number is required")
        .matches(/^\d+$/, "Phone number must contain only digits")
        .test("country-phone-limit", function (value) {
          const countryCode = this.parent?.countryCode || "+91";
          const phone = value || "";

          if (phone.length !== getPhoneNumberLimit(countryCode)) {
            return this.createError({
              message: `Phone number must be ${getPhoneNumberLimit(
                countryCode,
              )} digits`,
            });
          }

          return true;
        }),
      countryCode: Yup.string().required("Country Code is required"),
    }),
    autoSendReport: Yup.boolean().required("Auto Send Report is required"),
    autoCaseCreation: Yup.boolean().required("Auto Case Creation is required"),
    selectedCenters: Yup.array()
      .of(Yup.string())
      .when("autoSendReport", {
        is: true,
        then: (schema) =>
          schema.min(1, "Select at least 1 centre").required("Required"),
        otherwise: (schema) => schema.optional(),
      }),
    autoSendTo: Yup.string().when("autoSendReport", {
      is: true,
      then: (schema) => schema.required("Select one centre"),
      otherwise: (schema) => schema.optional(),
    }),
  });

  const {
    control,
    register,
    handleSubmit,
    watch,
    reset,
    setValue,
    trigger,
    formState: { isDirty, errors: formErrors },
  } = useForm({
    defaultValues,
    mode: "onChange",
    resolver: yupResolver(ProfileSchema),
  });
  const orgCountryCode = watch("orgCountryCode") || "+91";
  const supportCountryCode = watch("supportNumber.countryCode") || "+91";
  const orgPhoneLimit = getPhoneNumberLimit(orgCountryCode);
  const supportPhoneLimit = getPhoneNumberLimit(supportCountryCode);

  // watch values
  // const watchedValues = watch();

  useEffect(() => {
    const orgPhoneNumber = watch("orgPhoneNumber") || "";
    const trimmedPhoneNumber = sanitizePhoneNumber(
      orgPhoneNumber,
      orgCountryCode,
    );

    if (orgPhoneNumber !== trimmedPhoneNumber) {
      setValue("orgPhoneNumber", trimmedPhoneNumber, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  }, [orgCountryCode, setValue, watch]);

  // Migrated businesses never get auto-send - force it off and keep the
  // switch disabled below so it can't be re-enabled from this form.
  useEffect(() => {
    if (isMigratedUser && watch("autoSendReport")) {
      setValue("autoSendReport", false, { shouldDirty: true });
    }
  }, [isMigratedUser, setValue, watch]);

  useEffect(() => {
    const supportPhoneNumber = watch("supportNumber.phoneNumber") || "";
    const trimmedPhoneNumber = sanitizePhoneNumber(
      supportPhoneNumber,
      supportCountryCode,
    );

    if (supportPhoneNumber !== trimmedPhoneNumber) {
      setValue("supportNumber.phoneNumber", trimmedPhoneNumber, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  }, [supportCountryCode, setValue, watch]);

  // reset form only on initial load when orgProfile arrives
  useEffect(() => {
    if (!orgProfile) return;

    let countryCode = orgProfile?.country_code || "+91";
    let phoneNumber = orgProfile?.phone_no || "";

    const getAddressLatitude = (addressData) =>
      addressData?.latitude ??
      addressData?.lat ??
      addressData?.location?.coordinates?.[1] ??
      "";

    const getAddressLongitude = (addressData) =>
      addressData?.longitude ??
      addressData?.lng ??
      addressData?.location?.coordinates?.[0] ??
      "";

    const companyAddress =
      orgProfile?.company_data?.address || orgProfile?.company_data || {};
    const brandingItem = orgProfile?.business_settings?.co_branding?.find(
      (item) => item.reportType === "interpreted" && item.status === "active",
    );

    const rawBranding = brandingItem?.branding_by || "";

    const normalizedBranding =
      rawBranding === "organisation" || rawBranding === "organization"
        ? "organization"
        : rawBranding;
    const userData = {
      orgName: orgProfile?.business_name || "",
      orgCountryCode: countryCode,
      orgPhoneNumber: phoneNumber,
      orgType: orgProfile?.type || "",
      orgAddress: {
        fulladdress: orgProfile?.address?.fulladdress || "",
        area: orgProfile?.address?.area || "",
        floor: orgProfile?.address?.floor || "",
        pincode: orgProfile?.address?.pincode || "",
        city: orgProfile?.address?.city || "",
        state: orgProfile?.address?.state || "",
        country: orgProfile?.address?.country || "",
        latitude: getAddressLatitude(orgProfile?.address),
        longitude: getAddressLongitude(orgProfile?.address),
        district: orgProfile?.address?.district || "",
      },
      orgBillingDetails: {
        name: orgProfile?.company_data?.name || "",
        taxId: orgProfile?.company_data?.tax_id || "",
        pincode: orgProfile?.company_data?.pin || "",
        state: orgProfile?.company_data?.state || "",
        city: orgProfile?.company_data?.city || "",
        stateCode: orgProfile?.company_data?.code || "",
        address: {
          fulladdress: orgProfile?.company_data?.billing_address || "",
          latitude: getAddressLatitude(companyAddress),
          longitude: getAddressLongitude(companyAddress),
          district: orgProfile?.company_data?.district || "",
        },
      },
      supportNumber: {
        phoneNumber: orgProfile?.static_details?.support_no || "",
        countryCode: orgProfile?.static_details?.country_code || "+91",
      },
      branding_by: normalizedBranding,
      autoSendReport: orgProfile?.autoSendReport ?? true,
      autoCaseCreation: orgProfile?.autoCaseCreation ?? true,
      areSymptomsMandatory:
        orgProfile?.business_settings?.are_Symptoms_Mandatory ?? false,
      selectedCenters: orgProfile?.selectedCenters || [],
      autoSendTo:
        orgProfile?.interpretationReferTo?.userType === "center"
          ? orgProfile?.interpretationReferTo?.selectedId
          : orgProfile?.interpretationReferTo?.userType === "spandan-doctor"
            ? "spandan-expert"
            : "",
      logo: null,
    };
    reset(userData);
    setIsSameAsAddress(orgProfile?.company_data?.isSameAsAddress ?? false);
    setIsInitialLoad(false);
  }, [orgProfile, reset]);

  // when "same as address" checkbox is checked, copy fields
  // const watchedOrgName = watch("orgName");
  // const watchedOrgAddress = watch("orgAddress");
  // useEffect(() => {
  //   if (isSameAsAddress) {
  //     setValue("orgBillingDetails.name", watchedOrgName || "", {
  //       shouldDirty: true,
  //     });
  //     setValue(
  //       "orgBillingDetails.address.fulladdress",
  //       watchedOrgAddress?.fulladdress || "",
  //       { shouldDirty: true }
  //     );
  //     setValue("orgBillingDetails.city", watchedOrgAddress?.city || "", {
  //       shouldDirty: true,
  //     });
  //     setValue("orgBillingDetails.state", watchedOrgAddress?.state || "", {
  //       shouldDirty: true,
  //     });
  //     setValue("orgBillingDetails.pincode", watchedOrgAddress?.pincode || "", {
  //       shouldDirty: true,
  //     });
  //     setValue(
  //       "orgBillingDetails.address.floor",
  //       watchedOrgAddress?.floor || "",
  //       { shouldDirty: true }
  //     );
  //     setValue(
  //       "orgBillingDetails.address.area",
  //       watchedOrgAddress?.area || "",
  //       { shouldDirty: true }
  //     );
  //     setValue(
  //       "orgBillingDetails.address.country",
  //       watchedOrgAddress?.country || "",
  //       { shouldDirty: true }
  //     );
  //     setValue(
  //       "orgBillingDetails.address.latitude",
  //       watchedOrgAddress?.latitude || "",
  //       { shouldDirty: true }
  //     );
  //     setValue(
  //       "orgBillingDetails.address.longitude",
  //       watchedOrgAddress?.longitude || "",
  //       { shouldDirty: true }
  //     );
  //   }
  // }, [isSameAsAddress, watchedOrgName, watchedOrgAddress, setValue]);
  const handleSameAsToggle = (checked) => {
    setIsSameAsAddress(checked);
    const updateOptions = { shouldDirty: true, shouldValidate: true };

    if (checked) {
      const orgName = watch("orgName");
      const orgAddress = watch("orgAddress");

      setValue("orgBillingDetails.name", orgName || "", updateOptions);
      setValue(
        "orgBillingDetails.address.fulladdress",
        orgAddress?.fulladdress || "",
        updateOptions,
      );
      setValue("orgBillingDetails.city", orgAddress?.city || "", updateOptions);
      setValue(
        "orgBillingDetails.state",
        orgAddress?.state || "",
        updateOptions,
      );
      setValue(
        "orgBillingDetails.pincode",
        orgAddress?.pincode || "",
        updateOptions,
      );
      setValue(
        "orgBillingDetails.address.floor",
        orgAddress?.floor || "",
        updateOptions,
      );
      setValue(
        "orgBillingDetails.address.area",
        orgAddress?.area || "",
        updateOptions,
      );
      setValue(
        "orgBillingDetails.address.country",
        orgAddress?.country || "",
        updateOptions,
      );
      setValue(
        "orgBillingDetails.address.city",
        orgAddress?.city || "",
        updateOptions,
      );
      setValue(
        "orgBillingDetails.address.state",
        orgAddress?.state || "",
        updateOptions,
      );
      setValue(
        "orgBillingDetails.address.pincode",
        orgAddress?.pincode || "",
        updateOptions,
      );
      setValue(
        "orgBillingDetails.address.district",
        orgAddress?.district || "",
        updateOptions,
      );
      setValue(
        "orgBillingDetails.address.latitude",
        orgAddress?.latitude || "",
        updateOptions,
      );
      setValue(
        "orgBillingDetails.address.longitude",
        orgAddress?.longitude || "",
        updateOptions,
      );
    } else {
      setValue("orgBillingDetails.name", "", updateOptions);
      setValue("orgBillingDetails.address.fulladdress", "", updateOptions);
      setValue("orgBillingDetails.city", "", updateOptions);
      setValue("orgBillingDetails.state", "", updateOptions);
      setValue("orgBillingDetails.pincode", "", updateOptions);
      setValue("orgBillingDetails.address.latitude", "", updateOptions);
      setValue("orgBillingDetails.address.longitude", "", updateOptions);
    }
  };

  const updateLocation = (location) => {
    const address = {
      floor: location.floor || "",
      area: location.area || "",
      pincode: location.pincode || "",
      city: location.city || "",
      state: location.state || "",
      country: location.country || "",
      fulladdress: location.fulladdress || "",
      latitude: location.latitude || "",
      longitude: location.longitude || "",
      district: location.district || "",
    };
    setAddress(false);
    setValue("orgAddress", address, { shouldDirty: true });
    trigger("orgAddress.fulladdress");
  };

  const updateBillingLocation = (location) => {
    const address = {
      floor: location.floor || "",
      area: location.area || "",
      pincode: location.pincode || "",
      city: location.city || "",
      state: location.state || "",
      country: location.country || "",
      fulladdress: location.fulladdress || "",
      latitude: location.latitude || "",
      longitude: location.longitude || "",
      district: location.district || "",
    };
    setBillingAddress(false);
    setValue("orgBillingDetails.address", address, { shouldDirty: true });
    setValue("orgBillingDetails.pincode", address.pincode, {
      shouldDirty: true,
    });
    setValue("orgBillingDetails.city", address.city, { shouldDirty: true });
    setValue("orgBillingDetails.state", address.state, { shouldDirty: true });
    trigger("orgBillingDetails.address.fulladdress");
  };

  const handleLogoChange = (file) => {
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      setImageSrc(reader.result);
      setCropOpen(true);
    };
    reader.readAsDataURL(file);
  };

  const handleCroppedImage = (file) => {
    if (logoPreview) URL.revokeObjectURL(logoPreview);

    const previewUrl = URL.createObjectURL(file);
    setLogoPreview(previewUrl);
    setValue("logo", file, { shouldDirty: true });

    setCropOpen(false);
  };

  const validateField = async (fieldName) => {
    await trigger(fieldName);
  };

  const onSubmit = async (data) => {
    try {
      // Helper to clean string values
      const cleanValue = (val) =>
        typeof val === "string" ? formatText(val) : val;

      //  Helper to deeply clean objects
      const deepClean = (obj) => {
        if (Array.isArray(obj)) {
          return obj.map((item) => deepClean(item));
        } else if (typeof obj === "object" && obj !== null) {
          return Object.keys(obj).reduce((acc, key) => {
            acc[key] = deepClean(obj[key]);
            return acc;
          }, {});
        } else {
          return cleanValue(obj);
        }
      };

      //  Build raw args first
      console.log("data=-=-=-=-=->", data);
      const rawArgs = {
        name: data.orgName,
        countryCode: data.orgCountryCode,
        phoneNumber: data.orgPhoneNumber,
        organisationType: data.orgType,
        cobranding_enabled: !!data.branding_by,
        branding_by: data.branding_by,
        address: {
          floor: data.orgAddress?.floor || "",
          area: data.orgAddress?.area || "",
          fulladdress: data.orgAddress?.fulladdress,
          state: data.orgAddress?.state || "",
          city: data.orgAddress?.city || "",
          country: data.orgAddress?.country || "",
          pincode: data.orgAddress?.pincode || "",
          longitude: data.orgAddress?.longitude || "",
          latitude: data.orgAddress?.latitude || "",
          district: data.orgAddress?.district || "",
        },
        isSameAsAddress: isSameAsAddress,
        billingDetails: {
          billingName: data.orgBillingDetails?.name,
          billingAddress: data.orgBillingDetails?.address?.fulladdress,
          state: data.orgBillingDetails?.state,
          city: data.orgBillingDetails?.city,
          taxId: data.orgBillingDetails?.taxId,
          taxStateCode: data.orgBillingDetails?.stateCode,
          pin: data.orgBillingDetails?.pincode,
          latitude: data.orgBillingDetails?.address?.latitude || "",
          longitude: data.orgBillingDetails?.address?.longitude || "",
          address: {
            floor: data.orgBillingDetails?.address?.floor || "",
            area: data.orgBillingDetails?.address?.area || "",
            fulladdress: data.orgBillingDetails?.address?.fulladdress || "",
            state:
              data.orgBillingDetails?.address?.state ||
              data.orgBillingDetails?.state ||
              "",
            city:
              data.orgBillingDetails?.address?.city ||
              data.orgBillingDetails?.city ||
              "",
            country: data.orgBillingDetails?.address?.country || "",
            pincode:
              data.orgBillingDetails?.address?.pincode ||
              data.orgBillingDetails?.pincode ||
              "",
            longitude: data.orgBillingDetails?.address?.longitude || "",
            latitude: data.orgBillingDetails?.address?.latitude || "",
            district: data.orgBillingDetails?.address?.district || "",
          },
        },
        autoSendReport: data.autoSendReport,
        autoCaseCreation: data.autoCaseCreation,
        areSymptomsMandatory: data.areSymptomsMandatory,
        supportNumber: {
          phoneNumber: data.supportNumber?.phoneNumber,
          countryCode: data.supportNumber?.countryCode,
        },
        selectedCenters: data.selectedCenters,
        interpretationSendTo: {
          userType:
            data.autoSendTo === "spandan-expert" ? "spandan-doctor" : "center",
          selectedId:
            data.autoSendTo === "spandan-expert" ? "" : data.autoSendTo,
        },
      };

      //  Apply deep cleaning everywhere
      const args = deepClean(rawArgs);

      const formData = new FormData();
      formData.append("profileDetails", JSON.stringify(args));

      if (data.logo instanceof File) {
        formData.append("file", data.logo);
      }

      setButtonLoading(true);
      const response = await updataOrgSettings(formData);

      if (response.success) {
        toast.success("Profile updated successfully");
        setIsInitialLoad(true);
        setArgOrg({ reset: true });
      } else {
        toast.error(response.message || "Failed to update profile");
      }
    } catch (err) {
      toast.error(err?.message || "Something went wrong");
    } finally {
      setButtonLoading(false);
    }
  };

  const { states } = useGetIndianStates();
  const selectedBillingState = watch("orgBillingDetails.state");

  const selectedStateObj = states.find((s) => s.name === selectedBillingState);
  const { cities } = useGetCitiesByState(
    selectedBillingState,
    !!selectedBillingState,
  );

  const hasVirtualId = !!orgProfile?.virtualId;
  return (
    <>
      <Helmet>
        <title>Organization Settings</title>
      </Helmet>
      <form
        onSubmit={handleSubmit(onSubmit)}
        noValidate
        id="orgSettingsForm"
        style={{ paddingBottom: "80px", fontFamily: "Figtree" }}
      >
        <Box
          sx={{
            p: 2.5,
            mt: 2.5,
            backgroundColor: "var(--app-surface)",
            borderRadius: "12px",
            border: "1px solid var(--app-border)",
            boxShadow: "var(--app-shadow)",
          }}
        >
          <Typography
            sx={{
              fontSize: "20px",
              fontWeight: 700,
              fontFamily: "Figtree",
              mb: 2,
              color: "var(--app-text)",
            }}
          >
            Organisation Details
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", mt: 2 }}>
            {/* Left Column */}
            <Box
              sx={{
                width: { xs: "100%", md: "50%" },
                display: "flex",
                flexDirection: "column",
              }}
            >
              {/* Inner Column (replaces row) */}
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                {/* Name */}
                <Box
                  sx={{
                    mt: 1,
                    flex: { xs: "1 1 100%", md: "1 1 100%" },
                  }}
                >
                  {/* Label */}
                  <Typography
                    fontSize={14}
                    fontWeight={500}
                    mb="6px"
                    color="var(--app-text)"
                  >
                    Name <span style={{ color: "#d32f2f" }}>*</span>
                  </Typography>
                  <FormTextField
                    name="orgName"
                    register={register}
                    errors={formErrors?.orgName?.message}
                    required
                    placeholder="Enter Organisation Name"
                    onBlur={() => validateField("orgName")}
                  />
                </Box>

                {/* Contact Number */}
                <Box sx={{ width: "100%", mt: 2 }}>
                  <Typography
                    fontSize={14}
                    fontWeight={500}
                    mb="6px"
                    color="var(--app-text)"
                  >
                    {hasVirtualId ? "User ID" : "Contact Number"}
                    <span style={{ color: "#EB5757" }}> *</span>
                  </Typography>

                  {hasVirtualId ? (
                    <FormTextField
                      value={orgProfile?.virtualId || ""}
                      disabled
                      placeholder="Virtual ID"
                    />
                  ) : (
                    <Box
                      sx={{
                        display: "flex",
                        gap: "12px",
                        alignItems: "center",
                        maxWidth: "100%",
                        marginBottom: "10px",
                      }}
                    >
                      <Box sx={{ width: "83px" }}>
                        <FormCountryPicker
                          name="orgCountryCode"
                          control={control}
                          errors={formErrors.orgCountryCode?.message}
                          disabled
                        />
                      </Box>

                      <Box sx={{ flex: 1 }}>
                        <FormTextField
                          name="orgPhoneNumber"
                          register={register}
                          errors={formErrors.orgPhoneNumber}
                          placeholder="9876543210"
                          disabled
                        />
                      </Box>
                    </Box>
                  )}
                </Box>

                {/* Type */}
                <Box
                  sx={{
                    mt: 1,
                    flex: { xs: "1 1 100%", md: "1 1 100%" },
                  }}
                >
                  <Typography
                    fontSize={14}
                    fontWeight={500}
                    mb="6px"
                    color="var(--app-text)"
                  >
                    Type <span className="text-danger">*</span>
                  </Typography>

                  <FormControl fullWidth error={!!formErrors.orgType}>
                    <Autocomplete
                      options={organisationType}
                      value={watch("orgType") || null}
                      onChange={(event, value) => {
                        setValue("orgType", value || "", {
                          shouldDirty: true,
                        });
                        validateField("orgType");
                      }}
                      clearOnEscape
                      disablePortal
                      popupIcon={
                        <Icon
                          icon="eva:arrow-ios-downward-fill"
                          width="20"
                          height="20"
                          style={{
                            color: "var(--app-text-muted)",
                          }}
                        />
                      }
                      ListboxProps={{
                        style: {
                          maxHeight: "250px",
                          overflowY: "auto",
                        },
                      }}
                      sx={{
                        "& .MuiAutocomplete-popupIndicator": {
                          color: "var(--app-text-muted)",
                        },

                        "& .MuiAutocomplete-clearIndicator": {
                          color: "var(--app-text-muted)",
                        },

                        "& .MuiAutocomplete-paper": {
                          borderRadius: "12px",
                          marginTop: "6px",
                          boxShadow: "var(--app-shadow)",
                        },

                        "& .MuiAutocomplete-listbox": {
                          padding: "6px",

                          "&::-webkit-scrollbar": {
                            width: "6px",
                          },

                          "&::-webkit-scrollbar-thumb": {
                            backgroundColor: "#D1D5DB",
                            borderRadius: "10px",
                          },
                        },

                        "& .MuiAutocomplete-option": {
                          minHeight: "42px",
                          borderRadius: "8px",
                          fontSize: "14px",
                          fontFamily: "Figtree",

                          "&:hover": {
                            backgroundColor: "var(--app-surface-2)",
                          },

                          '&[aria-selected="true"]': {
                            backgroundColor: "var(--app-surface-2)",
                            fontWeight: 400,
                          },

                          '&[aria-selected="true"].Mui-focused': {
                            backgroundColor: "var(--app-border)",
                          },
                        },
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Select Type"
                          error={false}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              height: "52px",
                              borderRadius: "12px",
                              backgroundColor: "var(--app-surface)",

                              "& fieldset": {
                                border:
                                  "1px solid var(--app-border) !important",
                              },

                              "&:hover fieldset": {
                                border:
                                  "1px solid var(--app-border) !important",
                              },

                              "&.Mui-focused fieldset": {
                                border:
                                  "1px solid var(--app-border) !important",
                              },

                              "&.Mui-focused": {
                                boxShadow: "none !important",
                                outline: "none !important",
                              },
                            },

                            "& input": {
                              fontSize: "14px",
                              fontWeight: 400,
                              color: "var(--app-text)",
                            },

                            "& .MuiInputBase-input::placeholder": {
                              color: "var(--app-text-muted)",
                              opacity: 1,
                            },
                          }}
                        />
                      )}
                    />

                    {formErrors.orgType && (
                      <Typography color="error" variant="caption" mt={0.5}>
                        {formErrors.orgType.message}
                      </Typography>
                    )}
                  </FormControl>
                </Box>

                {/* Address - FIXED binding to orgAddress.fulladdress */}
                <Box
                  sx={{
                    mt: 2,
                    mb: 2,
                    flex: { xs: "1 1 100%", md: "1 1 100%" },
                  }}
                >
                  {/* Label */}
                  <Typography
                    fontSize={14}
                    fontWeight={500}
                    mb="6px"
                    color="var(--app-text)"
                  >
                    Address <span style={{ color: "#d32f2f" }}>*</span>
                  </Typography>

                  <Box sx={{ position: "relative", width: "100%" }}>
                    <TextField
                      fullWidth
                      variant="filled"
                      hiddenLabel
                      value={watch("orgAddress.fulladdress")}
                      onClick={() => setAddress(true)}
                      placeholder="Select Business Address"
                      error={!!formErrors.orgAddress?.fulladdress}
                      helperText={formErrors.orgAddress?.fulladdress?.message}
                      InputProps={{
                        readOnly: true,
                        disableUnderline: true,
                        sx: {
                          height: "48px",
                          borderRadius: "8px",
                          backgroundColor: "var(--app-surface)",
                          border: "1px solid var(--app-border)",
                          px: "4px",
                          fontFamily: "Figtree",

                          "& .MuiInputBase-input": {
                            fontSize: "14px",
                            fontWeight: 400,
                            color: "var(--app-text)",
                            cursor: "pointer",
                          },

                          "& .MuiInputBase-input::placeholder": {
                            fontSize: "14px",
                            fontWeight: 400,
                            color: "var(--app-text-muted)",
                            opacity: 1,
                          },

                          "&:hover": {
                            backgroundColor: "var(--app-surface)",
                          },

                          "&.Mui-focused": {
                            backgroundColor: "var(--app-surface)",
                          },
                        },
                      }}
                    />

                    <Icon
                      icon="mdi:map-marker"
                      width="22"
                      height="22"
                      style={{
                        position: "absolute",
                        right: "10px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        color: "var(--app-text-muted)",
                        pointerEvents: "none",
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            </Box>

            <Box
              sx={{
                width: { xs: "100%", md: "50%" },
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                mt: { xs: 3, md: 0 },
              }}
            >
              <ImageUploader
                initialUrl={orgProfile?.business_logo || null}
                value={watch("logo")}
                onChange={handleLogoChange}
              />

              <Box
                sx={{
                  position: "relative",
                  width: 200,
                  height: 200,
                  mb: "30px",
                }}
              />
            </Box>

            <Dialog
              open={address}
              onClose={() => setAddress(false)}
              fullWidth
              maxWidth="sm"
            >
              <DialogTitle
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  p: 1,
                }}
              >
                <IconButton onClick={() => setAddress(false)} size="small">
                  <Icon icon="mdi:close" width="22" height="22" />
                </IconButton>
              </DialogTitle>
              <DialogContent>
                <GoogleMapWithSearch onAddressChange={updateLocation} />
              </DialogContent>
            </Dialog>
          </Box>
        </Box>

        {/* Billing Details */}
        <BillingDetails
          isSameAsAddress={isSameAsAddress}
          handleSameAsToggle={handleSameAsToggle}
          formErrors={formErrors}
          register={register}
          validateField={validateField}
          watch={watch}
          setValue={setValue}
          states={states}
          cities={cities}
          selectedStateObj={selectedStateObj}
          isBillingManual={isBillingManual}
          setIsBillingManual={setIsBillingManual}
          setBillingAddress={setBillingAddress}
        />

        {/* Employee Support */}
        {/* <div className="card2 pt-20" style={{ marginTop: "20px" }}>
          <h5><strong>Employee Support</strong></h5>
          <div className="col-6">
            <div className="mt-4">
              <label>Mobile Number to display to Employees <span className="text-danger">*</span></label>
              <div className="row mt-2">
                <div className="col-3">
                  <div className="form-group">
                    <CountryCodePicker value={watch("supportNumber.countryCode")} onChange={(e) => { setValue("supportNumber.countryCode", e.value, { shouldDirty: true }); validateField("supportNumber.countryCode"); }} />
                    {formErrors.supportNumber?.countryCode && <span className="text-danger small">{formErrors.supportNumber?.countryCode?.message}</span>}
                  </div>
                </div>
                <div className="col-8">
                  <input type="text" className={`form-control ${formErrors.supportNumber?.phoneNumber ? "is-invalid" : ""}`} placeholder="Enter Mobile Number" {...register("supportNumber.phoneNumber")} onBlur={() => validateField("supportNumber.phoneNumber")} />
                  {formErrors.supportNumber?.phoneNumber && <div className="text-danger small mt-1">{formErrors.supportNumber?.phoneNumber?.message}</div>}
                </div>
              </div>
            </div>
          </div>
        </div> */}
        <Box
          sx={{
            mt: 2,
            px: 3,
            py: 2.5,
            borderRadius: "12px",
            border: "1px solid var(--app-border)",
            boxShadow: "var(--app-shadow)",
            backgroundColor: "var(--app-surface)",
            // border: "1px solid #E6E6E6",
          }}
        >
          {/* Title */}
          <Typography
            sx={{
              fontSize: "20px",
              fontWeight: 700,
              mb: 1.5,
              fontFamily: "Figtree",
              color: "var(--app-text)",
            }}
          >
            Employee Support
          </Typography>

          {/* Label */}
          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: 500,
              color: "var(--app-text)",
              mb: 1,
              fontFamily: "Figtree",
            }}
          >
            Mobile Number to display to employees
            <span style={{ color: "#EB5757" }}> *</span>
          </Typography>

          {/* Phone Row */}
          <Box
            sx={{
              display: "flex",
              alignItems: "start",
              gap: "12px",
              maxWidth: "520px",
            }}
          >
            {/* Country Picker */}
            <Box sx={{ width: "83px", overflow: "hidden" }}>
              <FormCountryPicker
                name="supportNumber.countryCode"
                control={control}
                errors={formErrors.supportNumber?.countryCode?.message}
              />
            </Box>

            {/* Phone Input */}
            <Box sx={{ flex: 1 }}>
              <FormTextField
                name="supportNumber.phoneNumber"
                register={register}
                errors={formErrors.supportNumber?.phoneNumber?.message}
                placeholder="9876543210"
                limit={10}
                allowSpecialChars={false}
                allowNumbers={true}
              />
            </Box>
          </Box>
        </Box>
        {!isMigratedUser && (
          <Box
            sx={{
              mt: 3,
              p: { xs: 2, md: 3 },
              borderRadius: "12px",
              border: "1px solid var(--app-border)",
              boxShadow: "var(--app-shadow)",
              backgroundColor: "var(--app-surface)",
            }}
          >
            <Box display="flex" flexDirection="column" gap={2}>
              <Typography
                variant="h6"
                fontWeight={700}
                fontFamily={"figtree"}
                fontSize={20}
                color="var(--app-text)"
              >
                Co-Branding
              </Typography>

              <Typography
                variant="subtitle1"
                fontWeight={600}
                fontFamily={"figtree"}
                fontSize={17}
                color="var(--app-text)"
              >
                Interpreted Reports will include branding of
              </Typography>

              <Box
                display="flex"
                flexDirection={{ xs: "column", md: "row" }} // key change
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", md: "center" }}
                gap={2}
              >
                <RadioGroup
                  row
                  name="branding_by"
                  value={watch("branding_by")}
                  onChange={(e) =>
                    setValue("branding_by", e.target.value, { shouldDirty: true })
                  }
                  sx={{
                    gap: 2, // reduce gap for mobile

                    "& .MuiFormControlLabel-label": {
                      fontSize: "15px",
                      fontWeight: 400,
                      color: "var(--app-text)",
                      fontFamily: "Figtree",
                    },

                    "& .MuiRadio-root": {
                      color: "var(--app-border)",
                    },

                    "& .Mui-checked": {
                      color: "#5751E9",
                    },
                  }}
                >
                  <FormControlLabel
                    value="organization"
                    control={<Radio />}
                    label="Organisation"
                  />
                  <FormControlLabel
                    value="center"
                    control={<Radio />}
                    label="Centre"
                  />
                </RadioGroup>

                <Button
                  variant="text"
                  disabled={!watch("branding_by")}
                  component="a"
                  href={
                    watch("branding_by") === "organization"
                      ? "https://cdn-for-sunfox-website.s3.ap-south-1.amazonaws.com/Org+co+branding+sample+report.pdf"
                      : "https://cdn-for-sunfox-website.s3.ap-south-1.amazonaws.com/Centre+co+branding+sample+report.pdf"
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    color: "#5751E9",
                    textDecoration: "underline",

                    // mobile full width + spacing
                    width: { xs: "100%", md: "auto" },
                    textAlign: { xs: "left", md: "right" },

                    cursor: !watch("branding_by") ? "not-allowed" : "pointer",

                    "&.Mui-disabled": {
                      color: "#5751E9",
                    },
                  }}
                >
                  Report Preview
                </Button>
              </Box>
            </Box>
          </Box>
        )}

        {/* -------------------- INTERPRETATION CREDITS -------------------- */}
        <Box sx={{ mt: 3 }}>
          <CreditBalanceCard
            credits={orgCredits}
            isUnlimited={orgIsUnlimitedCredits}
            isMigrated={isMigratedUser || orgCurrentPlan?.isMigrated === true}
            planName={orgCurrentPlan?.planName || orgCurrentPlan?.identifier}
            pricePerCredit={orgPricePerCredit}
            canTopUp={orgCanTopUp}
            onTopUpClick={openOrgTopUp}
          />
        </Box>

        {/* Automation Settings */}
        {!isMigratedUser && (
          <Box
            sx={{
              mt: 3,
              borderRadius: "14px",
              border: "1px solid #e2e0ed",
              boxShadow: "0 2px 10px rgba(0, 0, 0, 0.04)",
              backgroundColor: "#fff",
              overflow: "hidden",
            }}
          >
            <Box display="flex" flexDirection="column">
              {/* Card header */}
              <Box
                display="flex"
                flexDirection={{ xs: "column", md: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", md: "center" }}
                gap={2}
                sx={{ padding: "20px 22px" }}
              >
                <Box>
                  <Typography sx={{ fontSize: "17px", fontWeight: 700, color: "#22223b" }}>
                    Auto-send to Spandan Expert
                  </Typography>
                  <Typography sx={{ fontSize: "13px", color: "#77758a", marginTop: "2px" }}>
                    Send every new report for interpretation automatically
                  </Typography>
                </Box>

                {!isMigratedUser && orgAutoSendEnabled && orgOutOfCredits && (
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      height: "28px",
                      padding: "0 12px",
                      borderRadius: "999px",
                      border: "1px solid #F4D88A",
                      backgroundColor: "#FFFBF0",
                      color: "#8B6200",
                      fontSize: "12.5px",
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                      flexShrink: 0,
                    }}
                  >
                    Paused — no credits
                  </Box>
                )}
              </Box>

              <Box sx={{ borderTop: "1px solid #e2e0ed" }} />

              <Box sx={{ padding: "20px 22px", display: "flex", flexDirection: "column", gap: 3 }}>
                {/* -------------------- AUTO SEND REPORT -------------------- */}

                <Box
                  display="flex"
                  flexDirection={{ xs: "column", md: "row" }}
                  justifyContent="space-between"
                  alignItems={{ xs: "flex-start", md: "center" }}
                  gap={2}
                >
                  <Box flex={1}>
                    <Typography
                      variant="subtitle1"
                      fontWeight={600}
                      fontFamily={"figtree"}
                      fontsize={17}
                      color="var(--app-text)"
                    >
                      Auto-send new reports
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight={400}
                      fontFamily={"figtree"}
                      fontsize={13}
                      color="var(--app-text)"
                    >
                      On enabling, all generated reports will be automatically sent
                      for interpretation to the interpreter and centres you have
                      selected.
                    </Typography>
                  </Box>

                  <Box>
                    <Switch
                      onChange={(e) =>
                        setValue("autoSendReport", e.target.checked, {
                          shouldDirty: true,
                        })
                      }
                      checked={!isMigratedUser && watch("autoSendReport")}
                      disabled={isMigratedUser}
                    />
                  </Box>
                </Box>

                {/* Conditional Fields */}
                {!isMigratedUser && watch("autoSendReport") && (
                  <>
                    <Box
                      display="flex"
                      flexDirection={{ xs: "column", md: "row" }}
                      gap={3}
                    >
                      {/* Centres Tree */}
                      <Box flex={1}>
                        <Typography
                          sx={{
                            fontWeight: 500,
                            fontFamily: "figtree",
                            fontSize: 14,
                            color: "var(--app-text-muted)",
                            mb: 1,
                          }}
                        >
                          Auto send for interpretation in{" "}
                          <span style={{ color: "red" }}>*</span>
                        </Typography>

                        <CentersTreeView
                          user={{ centerIds: watch("selectedCenters") || [] }}
                          setUser={(updatedUser) => {
                            const ids = updatedUser.centerIds || [];
                            setValue("selectedCenters", ids, { shouldDirty: true });
                            if (!watch("autoSendReport")) {
                              setValue("autoSendReport", true, {
                                shouldDirty: true,
                              });
                            }
                            validateField("selectedCenters");
                          }}
                          handleSelectChange={(ids) => {
                            setValue("selectedCenters", ids, { shouldDirty: true });
                            if (!watch("autoSendReport")) {
                              setValue("autoSendReport", true, {
                                shouldDirty: true,
                              });
                            }
                            validateField("selectedCenters");
                          }}
                          orgSettings={true}
                          type="edit"
                          placeholder="Select centres you wish to auto send reports in"
                        />

                        {formErrors.selectedCenters && (
                          <Typography
                            variant="caption"
                            color="error"
                            mt={1}
                            display="block"
                          >
                            {formErrors.selectedCenters?.message}
                          </Typography>
                        )}
                      </Box>

                      {/* Centre Select */}
                      <Box flex={1}>
                        <Typography
                          sx={{
                            fontWeight: 500,
                            fontFamily: "figtree",
                            fontSize: 14,
                            color: "var(--app-text-muted)",
                            mb: 1,
                          }}
                        >
                          Auto send for interpretation to{" "}
                          <span style={{ color: "red" }}>*</span>
                        </Typography>

                        <CentreSelect
                          isMulti={false}
                          valueId={watch("autoSendTo")}
                          onChange={(val) => {
                            setValue("autoSendTo", val ? val.value : "", {
                              shouldDirty: true,
                            });

                            if (!watch("autoSendReport")) {
                              setValue("autoSendReport", true, {
                                shouldDirty: true,
                              });
                            }

                            validateField("autoSendTo");
                          }}
                          placeholder="Select the centre to auto send reports to"
                          UI_type="autoSendTo"
                          width="100%"
                          onlyInterpretationCentres
                        />

                        {formErrors.autoSendTo && (
                          <Typography
                            variant="caption"
                            color="error"
                            mt={1}
                            display="block"
                          >
                            {formErrors.autoSendTo?.message}
                          </Typography>
                        )}
                      </Box>
                    </Box>

                    <Box
                      sx={{
                        borderBottom: "1px solid var(--app-border)",
                        mt: 2,
                      }}
                    />
                  </>
                )}

                {!isMigratedUser && orgAutoSendEnabled && orgOutOfCredits && (
                  <Box
                    sx={{
                      backgroundColor: "#FFFBF0",
                      border: "1px solid #F4D88A",
                      borderRadius: "9px",
                      padding: "12px 14px",
                    }}
                  >
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#8B6200" }}>
                      Auto-send is on but paused.
                    </Typography>
                    <Typography sx={{ fontSize: 12.5, color: "#8B6200", mt: 0.5 }}>
                      The balance is 0, so new reports are not being sent. Skipped reports are marked on the
                      ECG Reports list and must be sent manually — they are not queued for later. Top up to
                      resume.
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        )}

        {/* Case Automation - hidden for migrated businesses */}
        {!isMigratedUser && (
          <Box
            sx={{
              mt: 3,
              p: { xs: 2, md: 3 },
              borderRadius: "12px",
              border: "1px solid var(--app-border)",
              boxShadow: "var(--app-shadow)",
              backgroundColor: "var(--app-surface)",
            }}
          >
            <Box display="flex" flexDirection="column" gap={3}>
              <Typography
                variant="h6"
                fontWeight={700}
                fontFamily={"figtree"}
                fontsize={20}
                color="var(--app-text)"
              >
                Automation Settings
              </Typography>

              {/* -------------------- AUTO CASE CREATION -------------------- */}

              <Box
                display="flex"
                flexDirection={{ xs: "column", md: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", md: "center" }}
                gap={2}
                mt={0}
                mb={1}
              >
                <Box flex={1}>
                  <Typography
                    variant="subtitle1"
                    fontWeight={600}
                    fontFamily={"figtree"}
                    fontsize={17}
                    color="var(--app-text)"
                  >
                    Auto case creation
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight={400}
                    fontFamily={"figtree"}
                    fontsize={13}
                    color="var(--app-text)"
                  >
                    On enabling, all critical reports collected at spoke centres
                    will be automatically referred to their corresponding hub
                    centres as cases.
                  </Typography>
                </Box>

                <Box>
                  <Switch
                    onChange={(e) =>
                      setValue("autoCaseCreation", e.target.checked, {
                        shouldDirty: true,
                      })
                    }
                    checked={watch("autoCaseCreation")}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        )}
        {!isMigratedUser && (
          <Box
            sx={{
              mt: 3,
              p: { xs: 2, md: 3 },
              borderRadius: "12px",
              border: "1px solid var(--app-border)",
              boxShadow: "var(--app-shadow)",
              backgroundColor: "var(--app-surface)",
            }}
          >
            {/* SYMPTOMS MANDATORY */}
            <Box
              display="flex"
              flexDirection={{ xs: "column", md: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", md: "center" }}
              gap={2}
              mt={0}
            >
              <Box flex={1}>
                <Typography
                  variant="h6"
                  fontWeight={700}
                  fontFamily={"figtree"}
                  fontsize={20}
                  color="var(--app-text)"
                >
                  Symptoms Mandatory
                </Typography>

                <Typography
                  variant="body2"
                  fontWeight={400}
                  fontFamily={"figtree"}
                  fontSize={13}
                  color="var(--app-text)"
                >
                  When enabled, symptoms will be mandatory while creating reports.
                </Typography>
              </Box>

              <Box>
                <Switch
                  checked={watch("areSymptomsMandatory")}
                  onChange={(e) =>
                    setValue("areSymptomsMandatory", e.target.checked, {
                      shouldDirty: true,
                    })
                  }
                />
              </Box>
            </Box>
          </Box>
        )}

        {/* Bottom save bar */}
        <div
          style={{
            position: "fixed",
            bottom: "0px",
            right: 0,
            left: 0,
            width: "100%",
            padding: "10px 0",
            backgroundColor: "var(--app-surface-2)",
            // boxShadow: "0 -4px 12px rgba(0,0,0,0.06)",
            zIndex: 1000,
            display: "flex",
            justifyContent: "flex-start",
            transition: "transform 0.35s ease-in-out",
            transform: open ? "translateX(10%)" : "translateX(0)",
          }}
        >
          {/* <button
            type="submit"
            className="btn btn-added-primary"
            disabled={!isDirty || buttonLoading}
            style={{
              width: "200px",
              marginLeft: "8%",
              transition: "all 0.35s ease-in-out",
              opacity: !isDirty || buttonLoading ? 0.6 : 1,
              cursor: !isDirty || buttonLoading ? "not-allowed" : "pointer",
            }}
          >
            {buttonLoading ? (
              <>
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                  aria-hidden="true"
                />
                <span className="sr-only">Loading...</span>
              </>
            ) : (
              "Save Details"
            )}
          </button> */}
          <Button
            type="submit"
            disabled={!isDirty || buttonLoading}
            sx={{
              width: "200px",
              marginLeft: "7%",
              height: "46px",
              borderRadius: "10px",
              fontWeight: 600,
              textTransform: "none",
              transition: "all 0.35s ease-in-out",

              background:
                !isDirty || buttonLoading
                  ? "var(--app-border)"
                  : "linear-gradient(90deg, #6C63FF, #5751E9)",

              color:
                !isDirty || buttonLoading ? "var(--app-text-muted)" : "#FFFFFF",

              boxShadow:
                !isDirty || buttonLoading
                  ? "none"
                  : "0px 4px 12px rgba(87, 81, 233, 0.3)",

              "&:hover": {
                background:
                  !isDirty || buttonLoading
                    ? "var(--app-border)"
                    : "linear-gradient(90deg, #6C63FF, #5751E9)",
              },
            }}
          >
            {buttonLoading ? (
              <>
                <CircularProgress size={18} sx={{ color: "#FFFFFF", mr: 1 }} />
                Saving...
              </>
            ) : (
              "Save Details"
            )}
          </Button>
        </div>
      </form>

      {/* Billing address dialog */}
      <Dialog
        open={billingAddress}
        onClose={() => setBillingAddress(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            p: 1,
          }}
        >
          <IconButton onClick={() => setBillingAddress(false)} size="small">
            <Icon icon="mdi:close" width="22" height="22" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <GoogleMapWithSearch onAddressChange={updateBillingLocation} />
        </DialogContent>
      </Dialog>
      <ImageCropperDialog
        open={cropOpen}
        imageSrc={imageSrc}
        aspect={1}
        onClose={() => setCropOpen(false)}
        onSave={handleCroppedImage}
      />
      <TopUpCreditsModal
        open={orgTopUpOpen}
        onClose={closeOrgTopUp}
        mode="topUp"
        currentBalance={orgCredits}
        pricePerCredit={orgPricePerCredit}
        loading={orgTopUpPaying}
        onPayTopUp={handleOrgPayTopUp}
      />
      <PaymentStatusModal
        open={orgStatusModal.open}
        onClose={closeOrgStatusModal}
        status={orgStatusModal.status}
        data={orgStatusModal.data}
        onPrimaryAction={retryOrgTopUp}
        onSecondaryAction={closeOrgStatusModal}
      />
    </>
  );
};

export default OrgSettings;
